import React, { useState, useRef, useEffect, useMemo } from "react";
import { Button, Select, Table, Tooltip } from "antd";
import { DownOutlined, PlusOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import styled from "styled-components";

const MAX_DISPLAY_ITEMS = 10;

const CustomPlus = (props) => {
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  // eslint-disable-next-line no-unused-vars
  const [visibleItems, setVisibleItems] = useState(MAX_DISPLAY_ITEMS);
  const tableContainerRef = useRef(null);
  const rowRefs = useRef([]);
  const debouncedSearchText = useMemo(
    () => debounce((text) => text.trim(), 300),
    []
  );

  const { visible, keyName, valueName, displayName, disabledThem } = props

  useEffect(() => {
    debouncedSearchText(searchText);
    setVisibleItems(MAX_DISPLAY_ITEMS);
  }, [debouncedSearchText, searchText]);

  const dataSource = useMemo(() => {
    if (!searchText) return props?.data;
    const searchWords = searchText.toLowerCase().split(/\s+/);
    return props?.data.filter((item) =>
      Object.values(item)
        .filter((value) => value != null)
        .some((value) =>
          searchWords.every((word) =>
            value
              .toString()
              .toLowerCase()
              .normalize("NFC")
              .includes(word.normalize("NFC"))
          )
        )
    );
  }, [props?.data, searchText]);

  const handleScroll = () => {
    if (!tableContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setVisibleItems((prev) =>
        Math.min(prev + MAX_DISPLAY_ITEMS, props.data.length)
      );
    }
  };

  useEffect(() => {
    if (!open || !dataSource?.length) return;
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
      const value = props.change ?? props.value;
      const index = dataSource.findIndex(
        (item) => item[props.valueName] === value
      );
      const scrollIndex = index !== -1 ? index : 0;

      setSelectedIndex(scrollIndex);
      requestAnimationFrame(() => {
        rowRefs.current[scrollIndex]?.scrollIntoView({
          behavior: "auto",
          block: "center",
        });
      });
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleScroll);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleKeyDown = (e) => {
    if (!open) return;
    let newIndex = selectedIndex;

    if (e.key === "ArrowDown") {
      newIndex = Math.min(selectedIndex + 1, dataSource.length - 1);
    } else if (e.key === "ArrowUp") {
      newIndex = Math.max(selectedIndex - 1, 0);
    } else if (e.key === "Enter" && selectedIndex !== -1) {
      handleSelect(dataSource[selectedIndex]);
      e.preventDefault();
    }

    if (newIndex !== selectedIndex) {
      setSelectedIndex(newIndex);
      rowRefs.current[newIndex]?.scrollIntoView({
        behavior: "auto",
        block: "center",
      });
    }
  };


  const handleSelect = (item) => {
    if (props.mode === "multiple" || props.mode === "tag") {
      const currentValues = Array.isArray(props.value) ? props.value : [];

      if (!item?.trangThai) return;

      if (!currentValues.includes(item[valueName] || item?.id)) {
        props.onChange?.([...currentValues, item[valueName] || item?.id || item.value]);
      }
    } else {
      props.onChange?.(item[valueName] || item?.id || item.value);
      setOpen(false);
    }
    setSearchText("");
  };

  const handleClear = () => {
    setSearchText("");
    props?.onChange?.(null);
  };

  const openModalPlus = (e) => {
    e.preventDefault();
    props?.openModalPlus();
  };

  return (
    <SelectWrapper visible={visible}>
      <Select
        {...props}
        showSearch
        allowClear
        onClear={handleClear}
        onSearch={setSearchText}
        onDropdownVisibleChange={setOpen}
        open={open}
        filterOption={false}
        onKeyDown={handleKeyDown}
        suffixIcon={<DownOutlined />}
        dropdownMatchSelectWidth={false}
        dropdownRender={() => (
          <div
            ref={tableContainerRef}
            style={{ maxHeight: "250px", overflowY: "auto" }}
            onMouseLeave={(event) => {
              const related = event.relatedTarget;
              if (
                tableContainerRef.current &&
                related instanceof Node &&
                !tableContainerRef.current.contains(related)
              ) {
                setOpen(false);
              }
            }}
          >
            <StyledTable
              columns={props?.columns}
              dataSource={dataSource}
              pagination={false}
              rowClassName={(record, index) =>
                index === selectedIndex ? "selected-row" : ""
              }
              onRow={(record, index) => ({
                ref: (el) => (rowRefs.current[index] = el),
                onClick: () => handleSelect(record),
              })}
            />
          </div>
        )}
      >
        {props?.data.map((x) => (
          <Select.Option
            key={x[keyName] || x?.id}
            value={x[valueName] || x?.id || x?.value}
          >
            {x[displayName] || x?.name || x?.label}
          </Select.Option>
        ))}
      </Select>
      {visible && (
        <Tooltip title="Thêm">
          <PlusButton onClick={openModalPlus} disabled={disabledThem}>
            <PlusOutlined />
          </PlusButton>
        </Tooltip>
      )}
    </SelectWrapper>
  );
};

export default CustomPlus;

const StyledTable = styled(Table)`
  .selected-row {
    background-color: #e6f7ff !important;
  }
  .ant-table-tbody > tr:hover > td {
    background-color: #e6f7ff !important;
  }
  .ant-table-thead > tr > th {
    font-weight: bold;
    text-align: center;
    position: sticky !important;
    top: 0;
    z-index: 2;
  }
  .ant-table-tbody > tr:hover {
    background-color: #e6f7ff !important;
  }
`;

const SelectWrapper = styled.div`
  display: flex;
  align-items: stretch;
  border-radius: 6px;
  ${({ visible }) =>
    visible &&
    `
    border: 1px solid #d9d9d9;
  `}
  overflow: hidden;
  .ant-select {
    flex: 1;
    border-radius: 0;
    border: none;
  }

  .ant-select-selector {
    border-right: 1px solid #d9d9d9;
  }
`;

const PlusButton = styled(Button)`
  border: none;
  border-left: 0px solid #d9d9d9;
  border-radius: 0;
  background: white;
  padding: 0 12px;
  display: flex;
  align-items: center;

  &:hover {
    color: #40a9ff;
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;
